/**
 * Enum-like class representing result category types for saved searches.
 * Provides predefined instances (`Communities` and `HousePlansAndHousingUnits`)
 * and utility methods for working with these types.
 */
export default class ResultCategoryType {

    /**
     * Gets the `Communities` result category type.
     * Represents a result category for Communities.
     *
     * @returns {ResultCategoryType} The `Communities` instance.
     */
    static get Communities() {  
        return this.getInstance('communities');
    }

    /**
     * Gets the `HousePlansAndHousingUnits` result category type.
     * Represents a result category for House Plans and Housing Units.
     *
     * @returns {ResultCategoryType} The `HousePlansAndHousingUnits` instance.
     */
    static get HousePlansAndHousingUnits() {
        return this.getInstance('housePlansAndHousingUnits');
    }

    /**
     * Retrieves a result category type instance based on the provided key.
     *
     * @param {string} key - The key representing the result category type
     * (e.g., 'communities', 'housePlansAndHousingUnits').
     * @returns {ResultCategoryType} The corresponding instance, or `undefined` if the key is invalid.
     */
    static fromKey(key) {
        const map = {
            communities: ResultCategoryType.Communities,
            housePlansAndHousingUnits: ResultCategoryType.HousePlansAndHousingUnits,
        };
        return map[key];
    }

    /**
     * Retrieves or creates an instance of `ResultCategoryType` for the given key.
     *
     * @param {string} key - The key representing the result category type.
     * @returns {ResultCategoryType} The corresponding instance.
     */
    static getInstance(key) {
        if (!this.instances[key]) {
            this.instances[key] = new ResultCategoryType(key);
        }
        return this.instances[key];
    }

    /**
     * Creates a new `ResultCategoryType` instance.
     *
     * @param {string} name - The name of the result category type.
     */
    constructor(name) {
        this.name = name;
    }

    /**
     * Converts the result category type to its string representation.
     *
     * @returns {string} The string representation of the result category type.
     */
    toString() {
        return `${this.name}`;
    }
}

// Stores instances of `ResultCategoryType` to ensure they are unique.
Object.defineProperty(ResultCategoryType, 'instances', { value: {} });